/**
 * Application config singleton. Populated by `Render.jsx` from `/api/config`
 * at boot and re-fetched on impersonation / workspace change.
 *
 * Consumers should prefer the React-aware `useConfig()` / `useFeature(key)`
 * hooks in `@/hooks/useConfig` over reading `get()` imperatively — the hooks
 * re-render their components when `set()` is called with a new value.
 */

export interface Config {
    version?: string;
    env?: string;
    definitions?: Record<string, Record<string, unknown>>;
    bank_transfer?: { beneficiary?: string; iban?: string; bic?: string };

    /**
     * Per-customer feature flags. The backend exposes these as
     * `{ feature_key: true/false }` on `/api/config`. A missing key counts
     * as `false` (feature gated off).
     */
    features?: Record<string, boolean>;
    [key: string]: unknown;
}

const CONFIG_CHANGED_EVENT = 'config-changed';

let configValues: Config = {};

export const get = (): Config => {
    return configValues;
};

export const set = (values: Config): void => {
    configValues = values;
    window.dispatchEvent(new CustomEvent(CONFIG_CHANGED_EVENT));
};

export const subscribeToConfig = (listener: () => void): () => void => {
    window.addEventListener(CONFIG_CHANGED_EVENT, listener);

    return () => {
        window.removeEventListener(CONFIG_CHANGED_EVENT, listener);
    };
};
